import {
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  type Cell,
  type Column,
  type Column_ColumnResizing,
  type Column_ColumnSizing,
  type Column_RowSorting,
  flexRender,
  type Header,
  type Header_ColumnResizing,
  type Header_ColumnSizing,
  type RowData,
  type TableFeatures,
} from '@tanstack/react-table'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ActionIcon,
  Box,
  Group,
  Table,
} from '@mantine/core'
import {
  ArrowDown,
  ArrowUp,
  Grip,
  GripVertical,
} from 'lucide-react'
import styles from './dataTable.module.scss'

// TanStack attaches feature methods through a conditional type over the table's feature set,
// which TypeScript defers inside a generic — so `Header<TFeatures, TData>` alone would lose
// getSize, getResizeHandler and getCanSort here. Intersecting the concrete per-feature
// interfaces keeps them visible, and still rejects a table that never registered the feature:
// a header from a table without column sizing is not assignable to these props.
type DraggableHeader<TFeatures extends TableFeatures, TData extends RowData> =
  Header<TFeatures, TData, unknown>
  & Header_ColumnSizing
  & Header_ColumnResizing
  & {
    column: Column<TFeatures, TData, unknown>
      & Column_RowSorting<TFeatures, TData>
      & Column_ColumnSizing
      & Column_ColumnResizing
  }

type SizedHeader<TFeatures extends TableFeatures, TData extends RowData> =
  Header<TFeatures, TData, unknown> & Header_ColumnSizing

type SizedCell<TFeatures extends TableFeatures, TData extends RowData> =
  Cell<TFeatures, TData, unknown> & {
    column: Column<TFeatures, TData, unknown> & Column_ColumnSizing
  }

interface DraggableHeaderCellProps<TFeatures extends TableFeatures, TData extends RowData> {
  header: DraggableHeader<TFeatures, TData>
  onKeyboardMove: (columnId: string, direction: -1 | 1) => void
}

export const DraggableHeaderCell = <TFeatures extends TableFeatures, TData extends RowData>({
  header,
  onKeyboardMove,
}: DraggableHeaderCellProps<TFeatures, TData>) => {
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
  } = useSortable({ id: header.column.id })
  const label = typeof header.column.columnDef.header === 'string'
    ? header.column.columnDef.header
    : header.column.id
  const cellStyle: CSSProperties = {
    backgroundColor: isDragging ? 'var(--mantine-color-body)' : undefined,
    cursor: header.column.getCanSort() ? 'pointer' : 'default',
    opacity: isDragging ? 0.85 : 1,
    overflow: 'hidden',
    position: 'relative',
    transform: CSS.Translate.toString(transform),
    transition: 'width 0.2s ease-in-out, transform 0.2s ease-in-out',
    userSelect: 'none',
    width: header.getSize(),
    zIndex: isDragging ? 2 : 0,
  }

  return (
    <Table.Th
      ref={setNodeRef}
      className={isDragging ? styles.draggingCell : styles.dragAlongCell}
      style={cellStyle}
      onClick={header.column.getToggleSortingHandler()}
    >
      <Group
        gap={4}
        wrap='nowrap'
        pr={18}
      >
        <ActionIcon
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label={`Reorder ${label} column`}
          className={isDragging
            ? `${styles.dragHandle} ${styles.isDragging}`
            : styles.dragHandle}
          variant='subtle'
          color='gray'
          size='xs'
          title='Use left and right arrow keys to reorder'
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault()
              event.stopPropagation()
              onKeyboardMove(header.column.id, event.key === 'ArrowLeft' ? -1 : 1)
            }
          }}
        >
          <Grip size={13} />
        </ActionIcon>

        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {header.column.getIsSorted() === 'asc' ? (
          <ArrowUp size={14} />
        ) : header.column.getIsSorted() === 'desc' ? (
          <ArrowDown size={14} />
        ) : null}
      </Group>

      {header.column.getCanResize() ? (
        <Box
          className={header.column.getIsResizing()
            ? `${styles.columnResizer} ${styles.isResizing}`
            : styles.columnResizer}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={(event) => event.stopPropagation()}
          title='Drag to resize column'
        >
          <GripVertical size={12} />
        </Box>
      ) : null}
    </Table.Th>
  )
}

interface DragAlongFilterCellProps<TFeatures extends TableFeatures, TData extends RowData> {
  header: SizedHeader<TFeatures, TData>
  children: ReactNode
}

export const DragAlongFilterCell = <TFeatures extends TableFeatures, TData extends RowData>({
  header,
  children,
}: DragAlongFilterCellProps<TFeatures, TData>) => {
  const {
    isDragging,
    setNodeRef,
    transform,
  } = useSortable({ id: header.column.id })

  return (
    <Table.Th
      ref={setNodeRef}
      className={isDragging ? styles.draggingCell : styles.dragAlongCell}
      style={{
        backgroundColor: isDragging ? 'var(--mantine-color-body)' : undefined,
        opacity: isDragging ? 0.85 : 1,
        position: 'relative',
        transform: CSS.Translate.toString(transform),
        transition: 'width 0.2s ease-in-out, transform 0.2s ease-in-out',
        width: header.getSize(),
        zIndex: isDragging ? 2 : 0,
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </Table.Th>
  )
}

interface DragAlongBodyCellProps<TFeatures extends TableFeatures, TData extends RowData> {
  cell: SizedCell<TFeatures, TData>
}

export const DragAlongBodyCell = <TFeatures extends TableFeatures, TData extends RowData>({
  cell,
}: DragAlongBodyCellProps<TFeatures, TData>) => {
  const {
    isDragging,
    setNodeRef,
    transform,
  } = useSortable({ id: cell.column.id })

  return (
    <Table.Td
      ref={setNodeRef}
      className={isDragging ? styles.draggingCell : styles.dragAlongCell}
      style={{
        backgroundColor: isDragging ? 'var(--mantine-color-body)' : undefined,
        fontSize: 16,
        opacity: isDragging ? 0.85 : 1,
        overflow: 'hidden',
        position: 'relative',
        textOverflow: 'ellipsis',
        transform: CSS.Translate.toString(transform),
        transition: 'width 0.2s ease-in-out, transform 0.2s ease-in-out',
        whiteSpace: 'nowrap',
        width: cell.column.getSize(),
        zIndex: isDragging ? 2 : 0,
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </Table.Td>
  )
}
